import React, { useState } from "react";
import { ArtistAvatarProps } from "../types";

/**
 * ArtistAvatar component - displays artist photo or initials
 * Supports multiple image URLs with fallbacks
 * Uses DiceBear API to generate consistent avatars based on artist name as final fallback
 */
const ArtistAvatar: React.FC<ArtistAvatarProps> = ({
  artist,
  size = "md",
  className = "",
}) => {
  const [imgError, setImgError] = useState(false);

  // Generate initials from artist name
  const getInitials = (name: string): string => {
    const names = name.trim().split(" ");
    if (names.length === 1) {
      return names[0].substring(0, 2).toUpperCase();
    }
    return (names[0][0] + names[names.length - 1][0]).toUpperCase();
  };

  // Generate a consistent avatar URL using DiceBear
  const getAvatarUrl = (name: string): string => {
    // Use initials style which creates letter-based avatars
    const seed = encodeURIComponent(name);
    return `https://api.dicebear.com/7.x/initials/svg?seed=${seed}&backgroundColor=3b82f6,8b5cf6,ec4899,f59e0b`;
  };

  const sizeClasses = {
    sm: "w-8 h-8 text-xs",
    md: "w-10 h-10 text-sm",
    lg: "w-12 h-12 text-base",
    xl: "w-16 h-16 text-lg",
  };

  // Prioritize image URLs: size-specific first, then fallback to generic, then DiceBear
  const getImageUrl = () => {
    if (size === "sm" && artist.small_image_url) return artist.small_image_url;
    if (size === "md" && artist.medium_image_url) return artist.medium_image_url;
    if (artist.image_url) return artist.image_url;
    return getAvatarUrl(artist.name);
  };

  const avatarSrc = getImageUrl();
  const initials = getInitials(artist.name);

  return (
    <div
      className={`${sizeClasses[size]} ${className} rounded-full overflow-hidden flex items-center justify-center bg-gradient-to-br from-blue-500 to-purple-600 text-white font-semibold shrink-0 relative`}
      title={artist.name}
    >
      {!imgError ? (
        <img
          src={avatarSrc}
          alt={artist.name}
          className="object-cover w-full h-full"
          onError={() => setImgError(true)}
        />
      ) : (
        <span>{initials}</span>
      )}
    </div>
  );
};

export default ArtistAvatar;
